import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import extractText from 'flarum/common/utils/extractText';
import styleSelectedText from 'flarum/common/utils/styleSelectedText';
import bbcodeMarkdownIt from './utils/bbcodeMarkdownIt';

// What's used here of fof/rich-text (its own modules, a lazy chunk,
// untyped from here) and of the Tiptap editor behind it.

const MARK = 'underline';
const modifierKey = () => navigator.userAgent.match(/Macintosh/) ? '⌘' : 'ctrl';
let underlineMark;

/**
 * Underlining as a mark of its own, kept as [u]…[/u] - flarum/bbcode's U,
 * the phpBB posts' underlining included (fof/rich-text leaves Tiptap's own
 * out: markdown has none). Pasted from elsewhere, only a <u> is kept - not
 * a web page's underlined links or styles.
 */
function createUnderlineMark() {
  const tiptap = window.flarum.reg.get('fof-rich-text', 'common/tiptap/tiptap');
  if (!tiptap) return null;
  return tiptap.Mark.create({
    name: MARK,
    // Outside a link when serialised, as the text colour - see
    // addTextColorButton.tsx. Tiptap's Link: 1000.
    priority: 1050,
    parseHTML() {
      return [{
        tag: 'u'
      }];
    },
    renderHTML() {
      return ['u', 0];
    },
    addKeyboardShortcuts() {
      return {
        'Mod-u': () => this.editor.commands.toggleMark(MARK)
      };
    }
  });
}

/**
 * An underline button in the composer's toolbar, right after strikethrough,
 * writing the same [u]…[/u] flarum/bbcode renders: in the rich editor
 * (most members'), the selection, or what's typed next, underlined as with
 * bold (⌘/ctrl-U too); in the plain one, the selection wrapped in those tags.
 *
 * fof/rich-text's modules by their ext: path, not imported - see
 * removeRichTextToolbarButtons.ts: a lazy chunk, loaded with the editor.
 */
export default function addUnderlineButton() {
  extend('ext:fof/rich-text/common/tiptap/TiptapEditorDriver', 'buildExtensions', function (items) {
    underlineMark ??= createUnderlineMark();
    if (underlineMark) items.add('champisUnderline', underlineMark);
  });
  const underlineMarkdownIt = bbcodeMarkdownIt({
    name: MARK,
    open: /^\[u\]/i,
    close: '[/u]',
    reopen: '[u]'
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder', 'buildTokenizer', function (md) {
    md.use(underlineMarkdownIt);
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder', 'buildTokens', function (tokens) {
    tokens[MARK] = {
      mark: MARK
    };
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownSerializerBuilder', 'buildMarks', function (marks) {
    marks[MARK] = {
      open: '[u]',
      close: '[/u]',
      mixable: true,
      expelEnclosingWhitespace: true
    };
  });

  // Right after strikethrough (75), before the text colour (72).
  extend('ext:fof/rich-text/common/components/TiptapMenu', 'items', function (items) {
    const MarkButton = window.flarum.reg.get('fof-rich-text', 'common/components/MarkButton');
    if (!MarkButton) return;
    items.add('champisUnderline', MarkButton.component({
      type: MARK,
      icon: 'fas fa-underline',
      tooltip: app.translator.trans('champis-forum.forum.underline.tooltip', {
        modifierKey: modifierKey()
      }),
      editor: this.attrs.editor,
      mark: MARK
    }), 74);
  });

  // flarum-markdown's own method, not in core's TextEditor typings -
  // after its strikethrough (700), before the text colour (650).
  extend('flarum/common/components/TextEditor', 'markdownToolbarItems', function (items) {
    const MarkdownButton = window.flarum.reg.get('flarum-markdown', 'common/components/MarkdownButton');
    if (!MarkdownButton) return;
    items.add('champisUnderline', MarkdownButton.component({
      title: extractText(app.translator.trans('champis-forum.forum.underline.tooltip', {
        modifierKey: modifierKey()
      })),
      icon: 'fas fa-underline',
      onclick: () => {
        const editor = this.attrs.composer.editor;

        // Its other fields defaulted by styleSelectedText() itself, as flarum-markdown has them.
        styleSelectedText(editor.el, {
          prefix: '[u]',
          suffix: '[/u]',
          trimFirst: true
        });
      }
    }), 690);
  });
}